import { DateTime, Interval } from "luxon";
import i18n from "@/i18n";

import {
  addInterventionHttp,
  updateInterventionHttp,
  deleteInterventionHttp,
  getMasterInterventionsHttp,
  countInterventionsByUserType,
  splitIntervention,
  fetchAllEventsDG2Http,
  getAdvSubInterventionsHttp,
  getInterventionAttendees,
  getAllInterventionsByUserIdAfterDateHttp,
  getInterventionQuotationsHttp,
} from "../../services/planning/InterventionServices.js";

const state = {
  events: [],
  masterInterventions: [],
  eventsByUserAfterDate: [],
  eventSelected: {
    x: null,
    id: null,
    duration: null,
    display: null,
    details: {},
    color: null,
    userType: null,
    location: null,
    course: null,
    userName: null,
    eventSiblings: [],
    attendees: null,
    slug: null,
    optionSlug: null,
  },
  eventsMultiArray: { city: null, option: null, validated: null, course: null }, // for filters
  startingDay: null,
  timePediod: null,
};

const getters = {
  getEvents(state) {
    return state.events;
  },
  getMasterEvents(state) {
    return state.masterInterventions;
  },
  getOnEventDisplay(state) {
    return state.events.filter((e) => e.display === true);
  },
  getEventSelected(state) {
    return state.eventSelected;
  },
  getEventsMultiarray(state) {
    return state.eventsMultiArray;
  },
  getStartingDay(state) {
    return state.startingDay;
  },
  getTimePeriod(state) {
    return state.timePediod;
  },
  getEventsByUserAfterDate(state) {
    return state.eventsByUserAfterDate;
  },
  getEventById: (state) => (id) => {
    return state.events.find((e) => e.details.id === id);
  },
};

const mutations = {
  SET_TIMEPERIOD: (state, data) => (state.timePediod = data),
  SET_EVENTS: (state, data) => (state.events = data),
  SET_MASTER_EVENTS: (state, data) => (state.masterInterventions = data),
  NEW_EVENT: (state, payload) => {
    state.events.push(payload);
  },
  NEW_MASTER_EVENT: (state, payload) => {
    state.masterInterventions.push(payload);
  },
  EDIT_EVENT: (state, payload) => {
    let index = state.events.findIndex((e) => e.id === payload.id);
    if (index !== -1) state.events.splice(index, 1, payload);
  },
  EDIT_MASTER_EVENT: (state, payload) => {
    let index = state.masterInterventions.findIndex((e) => e.id === payload.id);
    if (index !== -1) state.masterInterventions.splice(index, 1, payload);
  },
  REMOVE_EVENT: (state, payload) => {
    let index = state.events.findIndex((e) => e.details.id === payload);
    if (index !== -1) state.events.splice(index, 1);
  },
  REMOVE_MASTER_EVENT: (state, payload) => {
    let index = state.masterInterventions.findIndex((e) => e.id === payload);
    if (index !== -1) state.masterInterventions.splice(index, 1);
  },
  SET_EVENT_SELECTED: (state, data) => (state.eventSelected = data),
  SET_EVENTS_MULTIARRAY: (state, { city, option, validated, course }) => {
    state.eventsMultiArray.city = city;
    state.eventsMultiArray.option = option;
    state.eventsMultiArray.validated = validated;
    state.eventsMultiArray.course = course;
  },
  SET_STARTING_DAY: (state, data) => (state.startingDay = data),
  SET_EVENT_BY_USER_AFTER_DATE: (state, data) =>
    (state.eventsByUserAfterDate = data),
  SET_QUOTATIONS: (state, { quotations, eventId }) => {
    state.events.find((e) => e.details.id == eventId).details.quotations =
      quotations.data;
  },
};

const actions = {
  /**
   * This method is setting the data timePeriod for the calendar
   *
   * @param  {Vuex} {commit} necessary if call commit inside the method
   * @param  {String} timePeriod Type of calendar
   */
  async setTimePeriod({ commit }, timePeriod) {
    commit("SET_TIMEPERIOD", timePeriod);
  },
  /**
   * This method is setting the data startingDay for the calendar
   *
   * @param  {Vuex} {commit} necessary if call commit inside the method
   * @param  {Luxon Date} startingDay Luxon date when start the calendar
   */
  async fetchStartingDay({ commit }, startingDay) {
    commit("SET_STARTING_DAY", startingDay);
  },
  /**
   * After the client click on an event, this method will set the selected event to show in the EventInfoCard.vue which is the detail view of an event.
   * 
   * @param  {Vuex} {commit} necessary if call commit inside the method
   * @param  {Obecject - InterventionDto} eventSelected event that we want to display in the detail view

   */
  async setEventSelected({ commit }, eventSelected) {
    commit("SET_EVENT_SELECTED", eventSelected);
  },
  /**
   * This method is used to set event filter parameters
   *
   * @param  {Vuex} {commit} necessary if call commit inside the method
   * @param  {} {city filter events depending of the city chosen
   * @param  {} option filter events depending of the option chosen
   * @param  {} validated} filter events depending of the validation chosen
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("SET_EVENTS_MULTIARRAY", { city, option, validated })
   */
  async updateEventsMultiarray(
    { commit },
    { city, option, validated, course }
  ) {
    commit("SET_EVENTS_MULTIARRAY", { city, option, validated, course });
  },
  /**
   * This method loads only events that are 'masters'
   *
   * @param  {Vuex} {commit} necessary if call commit inside the method
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("SET_MASTER_EVENTS", response.data)
   */
  async loadEventsMasterCrud({ commit, dispatch }) {
    await getMasterInterventionsHttp()
      .then((response) => {
        commit("SET_MASTER_EVENTS", response.data);
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.eventAllError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },

  /**
   * This method verify if the event deleted was present in the array eventSiblings of other events
   * It will remove the deleted event Id from eventSiblings if it is the case.
   * Events can siblings only if they have the same user and the same course.
   *
   * @param  {Vuex} {getters} necessary if call getters inside the method
   * @param  {} eventId Id of the event that was deleted
   * @returns {} it is returning nothing but the array eventSiblings from other events will updated if they had eventId
   */
  async deleteSibling({ getters }, eventId) {
    getters.getEvents
      .filter((e) => e.eventSiblings.length > 0)
      .forEach((e) => {
        // delete a sibling
        if (e.eventSiblings.includes(eventId)) {
          let index = e.eventSiblings.findIndex((id) => id === eventId);
          e.eventSiblings.splice(index, 1);
        }
      });
  },
  /**
   * This method will create the event object necessary for the proper functioning of the calendar in the Front-End.
   *
   * @param  {Vuex} {rootGetters} necessary if call getters from another store
   * @param  {Object - interventionDto} evt event received from the Rest API that we need to transform in order to be able to be used in the calendar
   * @returns {Object - event Front-End} return an event that can be used by the calendar in the Front-End
   */
  async createFrontEventObject({ rootGetters }, evt) {
    let e = evt.e;
    const dateStart = new DateTime.fromISO(e.dateStart);
    const dateEnd = new DateTime.fromISO(e.dateEnd).plus({ days: 1 });
    const posY = await rootGetters["userModule/getOnUserDisplay"].findIndex(
      (u) => u.id === e.user.id
    );
    const duration = new Interval.fromDateTimes(dateStart, dateEnd).length(
      "days"
    );
    e.duration = duration;
    e.quotations = null;

    let event = {
      y: posY,
      x: e.dateStart,
      id: "INTER" + e.id,
      duration: duration,
      display: true,
      details: e,
      color: e.location.color,
      userType: rootGetters["userModule/getCalendar"],
      location: e.location.city,
      userName: e.user.fullName,
      course: e.course ? e.course.title : "Course Annuler",
      eventSiblings: e.eventSiblings,
      attendees: e.customers,
      optionSlug: e.optionSlug,
      slug: e.slug,
    };
    return event;
  },
  /**
   * This method will load events present between the interval (currentDate - 2 months) to (currentDate + 2 months)
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} getters necessary if call getters inside the method
   * @param  {Vuex} rootGetters necessary if call getters from another store
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {} currentDate the date where the client is located in the calendar
   * @returns {} it is returning nothing because the response will be load in the store directly
   */
  async loadEvents({ commit, getters, rootGetters, dispatch }, currentDate) {
    let timePrediod = rootGetters["eventModule/getTimePeriod"];

    let dateStart = null;
    let dateEnd = null;

    switch (timePrediod) {
      case "year":
        if (currentDate != null || currentDate != undefined)
          currentDate = DateTime.fromISO(currentDate).startOf("year");
        else
          currentDate = DateTime.fromISO(getters.getStartingDay)
            .startOf("year")
            .plus({ year: 1 });

        dateStart = currentDate.minus({ week: 1 }).toISODate();
        dateEnd = currentDate.plus({ year: 1 }).plus({ week: 1 }).toISODate();
        break;
      case "quarter":
        if (currentDate != null || currentDate != undefined)
          currentDate = DateTime.fromISO(currentDate).startOf("quarter");
        else
          currentDate = DateTime.fromISO(getters.getStartingDay)
            .startOf("quarter")
            .plus({ quarter: 1 });

        dateStart = currentDate.minus({ week: 1 }).toISODate();
        dateEnd = currentDate
          .plus({ quarter: 1 })
          .plus({ week: 1 })
          .toISODate();
        break;
      case "month":
        if (currentDate != null || currentDate != undefined)
          currentDate = DateTime.fromISO(currentDate).startOf("month");
        else
          currentDate = DateTime.fromISO(getters.getStartingDay)
            .startOf("month")
            .plus({ month: 1 });

        dateStart = currentDate.minus({ week: 1 }).toISODate();
        dateEnd = currentDate.plus({ month: 1 }).plus({ week: 1 }).toISODate();
        break;
      case "week":
        if (currentDate != null || currentDate != undefined)
          currentDate = DateTime.fromISO(currentDate).startOf("week");
        else
          currentDate = DateTime.fromISO(getters.getStartingDay)
            .startOf("week")
            .plus({ week: 1 });

        dateStart = currentDate.minus({ week: 1 }).toISODate();
        dateEnd = currentDate.plus({ week: 1 }).toISODate();
        break;
    }

    let count = await dispatch("countInterventions");
    //Note: conditions to not call every time getSubInterventionsHttp once we have the total of event per Calendar (userType)

    if (
      count >
      getters.getEvents.filter(
        (e) => e.userType === rootGetters["userModule/getCalendar"]
      ).length
    ) {
      const promise = await getAdvSubInterventionsHttp(
        rootGetters["userModule/getCalendar"],
        dateStart,
        dateEnd
      );

      const promises = promise.data.map((e) => {
        return dispatch("createFrontEventObject", { e });
      });

      state.events = await Promise.all(promises);
    }
  },
  /**
   * This method is calling the http call to add an event in the database and then in the Vuex store
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {Object - interventionDto} event object that we want to add in the database
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("NEW_EVENT", res)
   */
  async addEvent({ commit, dispatch }, event) {
    await addInterventionHttp(event)
      .then((response) => {
        let e = response.data;
        if (e.master === false) {
          dispatch("createFrontEventObject", { e }).then((res) => {
            commit("NEW_EVENT", res);
            let messageWS = {
              type: "ADD",
              id: JSON.stringify(res.id),
              event: JSON.stringify(res),
            };
            dispatch(
              "websocketModule/sendMessageToWebsocket",
              { messageWS },
              { root: true }
            );
          });
        } else {
          commit("NEW_MASTER_EVENT", e);
        }
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.eventAddSuccess"),
            typeAlert: "success",
          },
          { root: true }
        );
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.eventAddError"),
            typeAlert: "error",
          },
          { root: true }
        );

        if (error.response.data.length > 0) {
          error.response.data.forEach((error) => {
            for (const [key, value] of Object.entries(i18n.t("errorBackend"))) {
              if (error.type === key) {
                for (const [keyE, valueE] of Object.entries(value)) {
                  if (keyE === "message")
                    dispatch(
                      "notificationModule/showErrorMessages",
                      { valueE },
                      { root: true }
                    );
                }
              }
            }
          });
        }
      });
  },
  /**
   * This method is calling the http call to edit an event in the database and then in the VueX store
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {Object - interventionDto} event object that we want to edit in the database
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("EDIT_EVENT", res)
   */
  async editEvent({ commit, dispatch }, event) {
    const editedEvent = {
      id: event.id,
      idDg2: event.idDg2,
      slug: event.slug,
      comment: event.comment,
      locationId: event.locationId,
      locationIdDg2: event.locationIdDg2,
      courseId: event.courseId,
      courseIdDg2: event.courseIdDg2,
      userId: event.userId,
      attendeesCount: event.attendeesCount,
      type: event.type,
      validated: event.validated,
      dateStart: event.dateStart,
      dateEnd: event.dateEnd,
      timeStart: event.timeStart,
      timeEnd: event.timeEnd,
      masterInterventionId:
        event.masterIntervention == null ? 0 : event.masterInterventionId,
      isMaster: event.isMaster,
      version: event.version,
    };

    await updateInterventionHttp(editedEvent)
      .then((response) => {
        let e = response.data;
        if (e.master === false) {
          dispatch("createFrontEventObject", { e }).then((res) => {
            commit("EDIT_EVENT", res);
            dispatch("setEventSelected", res);
            let messageWS = {
              type: "EDIT",
              id: JSON.stringify(res.id),
              event: JSON.stringify(res),
            };
            dispatch(
              "websocketModule/sendMessageToWebsocket",
              { messageWS },
              { root: true }
            );
          });
        } else {
          commit("EDIT_MASTER_EVENT", e);
          dispatch(
            "notificationModule/showNotification",
            {
              messageAlert: i18n.t("messageStores.eventEditSuccess"),
              typeAlert: "success",
            },
            { root: true }
          );
        }
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.eventEditError"),
            typeAlert: "error",
          },
          { root: true }
        );

        // if (error.response.data.length > 0) {
        //   error.response.data.forEach((error) => {
        //     for (const [key, value] of Object.entries(i18n.t("errorBackend"))) {
        //       if (error.type === key) {
        //         for (const [keyE, valueE] of Object.entries(value)) {
        //           if (keyE === "message")
        //             dispatch(
        //               "notificationModule/showErrorMessages",
        //               { valueE },
        //               { root: true }
        //             );
        //         }
        //       }
        //     }
        //   });
        // }
      });
  },
  /**
   * This method will create the event object necessary for the proper functioning of the calendar in the Front-End.
   * It is also sending a message to the WebSocket with the edited event.
   * This method is used while calling restore method for memento.
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {Object - interventionDto} e event that was restored
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("EDIT_EVENT", res)
   */
  async editEventNoHttpCall({ commit, dispatch }, e) {
    if (e.master === false) {
      dispatch("createFrontEventObject", { e }).then((res) => {
        commit("EDIT_EVENT", res);
        let messageWS = {
          type: "EDIT",
          id: JSON.stringify(res.id),
          event: JSON.stringify(res),
        };
        dispatch(
          "websocketModule/sendMessageToWebsocket",
          { messageWS },
          { root: true }
        );
      });
    } else {
      commit("EDIT_MASTER_EVENT", e);
    }
  },
  /**
   * This method is calling the http call to delete an event in the database and then in the Vuex store
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {Object - interventionDto} id object that we want to delete in the database
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("REMOVE_EVENT", event.id)
   */
  async deleteEvent({ commit, dispatch }, id) {
    await deleteInterventionHttp(id)
      .then((response) => {
        if (response.status === 202) {
          commit("REMOVE_EVENT", id);
          let messageWS = {
            type: "DELETE",
            id: JSON.stringify(id),
            event: null,
          };
          dispatch(
            "websocketModule/sendMessageToWebsocket",
            { messageWS },
            { root: true }
          );
          dispatch(
            "notificationModule/showNotification",
            {
              messageAlert: i18n.t("messageStores.eventIdDeleteSuccess", {
                id: id,
              }),
              typeAlert: "success",
            },
            { root: true }
          );
        }
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.eventDeleteError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
  /**
   * This method is calling the http call to delete an event in the database and then in the Vuex store
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {Object - interventionDto} id object that we want to delete in the database
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("REMOVE_EVENT", event.id)
   */
  async deleteEventMaster({ commit, dispatch }, id) {
    await deleteInterventionHttp(id)
      .then((response) => {
        if (response.status === 202) {
          commit("REMOVE_MASTER_EVENT", id);
          dispatch(
            "notificationModule/showNotification",
            {
              messageAlert: i18n.t("messageStores.masterEventIdDeleteSuccess", {
                id: id,
              }),
              typeAlert: "success",
            },
            { root: true }
          );
        }
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.eventDeleteError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
  /**
   * This method will give the number of events depending of the calendar selecteced (trainer, salesperson, admin, etc)
   *
   * @param  {Vuex} {rootGetters} necessary if call getters from another store
   * @returns {int} it is returning the number of events depending of the calendar selecteced
   */
  async countInterventions({ rootGetters }) {
    let nb = await countInterventionsByUserType(
      rootGetters["userModule/getCalendar"]
    )
      .then((response) => {
        return response.data.nb;
      })
      .catch((error) => {
        return error;
      });
    return nb;
  },
  /**
   * This method will create children interventions under a master intervention
   *
   * @param  {Vuex} {dispatch necessary if call dispatch inside the method
   * @param  {Vuex} commit} necessary if call commit inside the method
   * @param  {} {interventionId
   * @param  {} dateRanges}
   * @returns {}
   */
  async splitIntervention(
    { dispatch, commit },
    { interventionId, dateRanges }
  ) {
    await splitIntervention(interventionId, dateRanges).then((res) => {
      res.data.forEach((intervention) => {
        if (state.events.find((i) => i.id === intervention.id)) {
          dispatch("editEventNoHttpCall", intervention);
        } else {
          if (intervention.master === false) {
            dispatch("createFrontEventObject", { e: intervention }).then(
              (res) => {
                commit("NEW_EVENT", res);
                let messageWS = {
                  type: "ADD",
                  id: JSON.stringify(res.id),
                  event: JSON.stringify(res),
                };
                dispatch(
                  "websocketModule/sendMessageToWebsocket",
                  { messageWS },
                  { root: true }
                );
              }
            );
          } else {
            commit("NEW_MASTER_EVENT", intervention);
          }
        }
      });
    });
  },
  /**
   * This method is calling the http call to fetch users from the webservice Wdg2 (create or update depending if the course exists or not)
   *
   * @param  {Vuex} {dispatch} necessary if call dispatch inside the method
   * @param  {Object - LogInUser} logInUser user dg2
   * @param  {Number} year selected year import
   * @returns {} it is returning nothing. The database is updated directly in the Back-End when the method is called.
   */
  async fetchEventsDG2({ dispatch }, { logInUser, year }) {
    await fetchAllEventsDG2Http(logInUser, year)
      .then((response) => {
        if (response.status === 202) {
          dispatch("loadEvents");
          dispatch(
            "notificationModule/showNotification",
            {
              messageAlert: i18n.t("messageStores.userAllSuccess"),
              typeAlert: "success",
            },
            { root: true }
          );
        }
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.userAllError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
  /**
   * This method will make a request HTPP Get to get all Interventions for given User starting after given date and store them into
   * eventsByUserAfterDate
   * @param {number} userId the Id of the user which Internventions we want
   * @param {string} dateStart The minimum starting date of the selected interventions we want
   */
  async getAllInterventionsByUserIdAfterDate(
    { dispatch },
    { userId, dateStart }
  ) {
    const promise = await getAllInterventionsByUserIdAfterDateHttp(
      userId,
      dateStart
    );

    const promises = await promise.data.map((e) => {
      return dispatch("createFrontEventObject", { e });
    });

    state.eventsByUserAfterDate = await Promise.all(promises);
  },
  /**
   * This return quotations in DG2 for the selected intervention
   * @param {event} event front intervention
   * @returns a list of quotations
   */
  async getQuotations({ commit }, event) {
    if (event.details.quotations == null) {
      let eventId = event.details.id;

      await getInterventionQuotationsHttp(
        event.details.course.idDg2,
        event.details.location.idDg2
      ).then((quotations) => {
        commit("SET_QUOTATIONS", { quotations, eventId });
      });
    }
  },
};

const methods = {
  /**
   * This method checks if a given event is overlaping with existing interventions
   *
   * @param {string} startDate date of the beggining of event to test
   * @param {Object} event event to test
   * @param {Object} user user whose interventions we want to test
   * @returns a boolean true if there are no overlaping intervention, false if there are overlaping intervention
   */
  intervalIsEmpty(startDate, event, user) {
    const duration = event.duration - 1;
    let start = new Date(startDate);
    let end = new Date(startDate);
    end.setDate(end.getDate() + duration);
    let isEmpty = true;

    state.events
      .filter((e) => e.details.user.id === user.id && e.id !== event.id)
      .forEach((e) => {
        const eStart = new Date(e.details.dateStart);
        const eEnd = new Date(e.details.dateEnd);

        //Test if interventions overlap
        if (eStart <= end && eEnd >= start) {
          if (e.course !== event.course) {
            isEmpty = false;
          }
          //Check if same course that they have same start day and end day
          if (
            e.course === event.course &&
            (eStart.getDate() !== start.getDate() ||
              eEnd.getDate() !== end.getDate())
          ) {
            isEmpty = false;
          }
        }
      });
    return isEmpty;
  },
  /**
   * This method checks if a given event is overlaping with existing interventions
   * @param {string} startDate date of the beggining of event to test
   * @param {string} endDate date of the end of event to test
   * @param {int} eventId id of the event to test (this parameter is optional in case of check when creating an event for example)
   * @param {string} eventCourseName the name of the course of the event to test
   * @param {Object} user user whose interventions we want to test
   * @returns a boolean true if there are no overlaping intervention, false if there are overlaping intervention
   */
  intervalIsEmptyStartAndEnd(
    startDate,
    endDate,
    eventId,
    eventCourseName,
    user
  ) {
    eventId = eventId || null;
    let start = new Date(startDate);
    let end = new Date(endDate);
    let isEmpty = true;

    state.events
      .filter((e) => e.details.user.id === user.id && e.id !== eventId)
      .forEach((e) => {
        const eStart = new Date(e.details.dateStart);
        const eEnd = new Date(e.details.dateEnd);

        //Test if interventions overlap
        if (eStart <= end && eEnd >= start) {
          if (e.course !== eventCourseName) {
            isEmpty = false;
          }
          //Check if same course that they have same start day and end day
          if (
            e.course === eventCourseName &&
            (eStart.getDate() !== start.getDate() ||
              eEnd.getDate() !== end.getDate())
          ) {
            isEmpty = false;
          }
        }
      });
    return isEmpty;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  methods,
};
